import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { catalogPublisherRefusal, errors, paxRoute, StreamResponse } from "../../common";
import {
  CatalogBlobParams,
  CatalogJob,
  CatalogJobParams,
  CatalogProblems,
  PublishCatalogVersionRequest,
  PublishCatalogVersionResponse,
  PutCatalogBlobRequest,
  PutCatalogBlobResponse,
} from "./schemas";

export const getBlob = oc
  .meta(
    paxRoute({
      auth: { kind: "session", note: "Immutable catalog blobs are public and read-only." },
      minimumTier: null,
      cost: "read",
    }),
    openapi({
      method: "GET",
      path: "/api/catalog/v1alpha1/blobs/{sha256}",
      operationId: "catalog.blobs.get",
      summary: "Read an immutable catalog blob.",
      description: "Serves content-addressed catalog bytes for maps and other published assets.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      responseBodyHint: "octet-stream",
    }),
  )
  .errors({ ...errors.badRequest, ...errors.notFound })
  .input(z.object({ params: CatalogBlobParams }))
  .output(StreamResponse);

export const setBlob = oc
  .meta(
    paxRoute({ auth: { kind: "catalog-publisher" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/catalog/v1alpha1/blobs/{sha256}",
      operationId: "catalog.blobs.set",
      summary: "Put a catalog blob.",
      description:
        "Stores one file by its SHA-256, ready for a catalog version to name. A file the environment already has, stored or waiting in the uploads bucket, isn't sent again.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...catalogPublisherRefusal })
  .input(z.object({ params: CatalogBlobParams, body: PutCatalogBlobRequest }))
  .output(PutCatalogBlobResponse);

export const publishVersion = oc
  .meta(
    paxRoute({ auth: { kind: "catalog-publisher" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/catalog/v1alpha1/versions/publish",
      operationId: "catalog.versions.publish",
      summary: "Publish a catalog version.",
      description:
        "Records a published version of any kind from files already put, owned by a persona, without an editor or moderation. A version matching the item's live version is left alone. Catalog content is prepared and checked before publishing. A basemap's large public file copies run in a job, which the response names; the version is live once it succeeds.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...catalogPublisherRefusal,
    NOT_FOUND: { message: "The owner, or a file the version names, isn't in this environment." },
    CONFLICT: { message: "The slug belongs to another item, or the id to another slug." },
    UNPROCESSABLE_CONTENT: {
      message: "The version isn't valid for its kind.",
      data: CatalogProblems,
    },
    SERVICE_UNAVAILABLE: { message: "A service publishing needs didn't answer. Try again." },
  })
  .input(z.object({ body: PublishCatalogVersionRequest }))
  .output(PublishCatalogVersionResponse);

export const getJob = oc
  .meta(
    paxRoute({ auth: { kind: "catalog-publisher" }, minimumTier: "suspicious", cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/catalog/v1alpha1/jobs/{jobId}",
      operationId: "catalog.jobs.get",
      summary: "Get a catalog job.",
      description: "Reads a catalog publish's job from its Workflow instance.",
      inputStructure: "detailed",
      successDescription: "The job.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...catalogPublisherRefusal,
    NOT_FOUND: { message: "There's no job with that id." },
  })
  .input(z.object({ params: CatalogJobParams }))
  .output(CatalogJob);
